import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import styles from "./ProviderDashboard.module.css";
import TodayScheduleCard from "./TodayScheduleCard";
import NewClientsCard from "./NewClientsCard";

const EMPTY_METRICS = {
  scheduledSessionsThisWeek: 0,
  completedSessionsThisWeek: 0,
  activeClients: 0,
  pendingIntakes: 0,
};

const ProviderDashboard = () => {
  const user = useSelector((state) => state?.user);
  const authToken = user?.user?.token;

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [todayDateLabel, setTodayDateLabel] = useState("");
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [newClients, setNewClients] = useState([]);
  const [newClientsCount, setNewClientsCount] = useState(0);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/users/provider-dashboard?timezone=${encodeURIComponent(timezone)}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );

        if (response.data?.success) {
          const data = response.data.data || {};
          setMetrics({ ...EMPTY_METRICS, ...(data.metrics || {}) });
          setTodayDateLabel(data.todayDateLabel || "");
          setTodayAppointments(data.todayAppointments || []);
          setNewClients(data.newClients || []);
          setNewClientsCount(data.newClientsCount || 0);
        }
      } catch (error) {
        console.error("Error fetching provider dashboard:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [authToken]);

  const pendingIntakeLabel =
    metrics.pendingIntakes === 1
      ? "1 pending intake"
      : `${metrics.pendingIntakes} pending intake`;

  return (
    <div className={`app-page ${styles.page}`}>
      <h1 className={styles.pageTitle}>Dashboard</h1>

      <div className={styles.metricsRow}>
        <article className={styles.metricCard}>
          <p className={styles.metricLabel}>Scheduled Sessions</p>
          <p className={styles.metricValue}>
            {isLoading ? "—" : metrics.scheduledSessionsThisWeek}
          </p>
          <p className={styles.metricSubtext}>This week</p>
        </article>

        <article className={styles.metricCard}>
          <p className={styles.metricLabel}>Completed Sessions</p>
          <p className={styles.metricValue}>
            {isLoading ? "—" : metrics.completedSessionsThisWeek}
          </p>
          <p className={styles.metricSubtext}>This week</p>
        </article>

        <article className={styles.metricCard}>
          <p className={styles.metricLabel}>Active Clients</p>
          <p className={styles.metricValue}>
            {isLoading ? "—" : metrics.activeClients}
          </p>
          <p
            className={
              !isLoading && metrics.pendingIntakes > 0
                ? styles.metricSubtextHighlight
                : styles.metricSubtext
            }
          >
            {isLoading ? "—" : pendingIntakeLabel}
          </p>
        </article>
      </div>

      <div className={styles.widgetsRow}>
        <TodayScheduleCard
          appointments={todayAppointments}
          dateLabel={todayDateLabel}
          isLoading={isLoading}
        />

        <NewClientsCard
          clients={newClients}
          count={newClientsCount}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default ProviderDashboard;
